import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { withStorageSync } from '@angular-architects/ngrx-toolkit';
import { initialStatus, LoadingStatus } from '@bcef/ia-shared';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { concatMap, debounceTime, EMPTY, pipe, switchMap, tap } from 'rxjs';

import { ErrorStore } from '@shared/store/error.store';

import { buildLissageRequest } from '../mappers/cehr-request.mappers';
import { CehrLissageRequest, CehrSimulationRequest } from '../models/cehr-simulation-request';
import {
  CehrLissageResponse,
  CehrSimulationResponse,
} from '../models/cehr-simulation-response.model';
import { CehrSimulationService } from '../services/cehr-simulation.service';

export interface CehrState {
  cehrSimulationRequest: Partial<CehrSimulationRequest>;
  cehrSimulationResponse: Partial<CehrSimulationResponse>;
  cehrLissageRequest: Partial<CehrLissageRequest>;
  cehrLissageResponse: Partial<CehrLissageResponse>;
  loadingStatus: LoadingStatus;
}
export const initialState: CehrState = {
  cehrSimulationRequest: {},
  cehrSimulationResponse: {},
  cehrLissageRequest: {},
  cehrLissageResponse: {},
  loadingStatus: initialStatus,
};

export interface SubmitPayload {
  enabledLissage: boolean;
  rfrNMinus1: number | undefined;
  rfrNMinus2: number | undefined;
}

export const SEND_CEHR_FORM_DEBOUNCE_MILLIS = 300;

export const CehrStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withStorageSync({ key: 'ia-simulateur-epargne:cehr', storage: () => sessionStorage }),
  withMethods(
    (
      store,
      cehrSimulationService = inject(CehrSimulationService),
      errorStore = inject(ErrorStore),
      router = inject(Router),
    ) => ({
      sendCehrForm: rxMethod<SubmitPayload>(
        pipe(
          tap(() =>
            patchState(store, (state) => ({
              cehrLissageResponse: {},
              loadingStatus: {
                ...state.loadingStatus,
                isLoading: true,
              },
            })),
          ),
          debounceTime(SEND_CEHR_FORM_DEBOUNCE_MILLIS),
          switchMap((payload) => {
            const cehrReq = store.cehrSimulationRequest();
            return cehrSimulationService.postCehrSimulation(cehrReq).pipe(
              tapResponse({
                next: (cehrSimulationResponse) => {
                  patchState(store, (state) => ({
                    cehrSimulationResponse,
                    loadingStatus: { ...state.loadingStatus, isLoaded: true },
                  }));
                  router.navigate(['cehr', 'summary']);
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setError(error);
                  patchState(store, (state) => ({
                    loadingStatus: { ...state.loadingStatus, error },
                  }));
                  router.navigate(['erreur']);
                },
                finalize: () =>
                  patchState(store, (state) => ({
                    loadingStatus: { ...state.loadingStatus, isLoading: false },
                  })),
              }),
              concatMap((cehrRes) => {
                const shouldCallLissage =
                  payload.enabledLissage && cehrRes.liableforCEHR && cehrRes.maxSavingAmount > 0;
                if (!shouldCallLissage) return EMPTY;

                const lissageReq = buildLissageRequest(cehrRes, cehrReq, {
                  rfrNMinus1: payload.rfrNMinus1,
                  rfrNMinus2: payload.rfrNMinus2,
                });

                return cehrSimulationService.postCehrSimulationLissage(lissageReq).pipe(
                  tapResponse({
                    next: (lissageRes) =>
                      patchState(store, (state) => ({
                        cehrLissageResponse: lissageRes,
                        loadingStatus: { ...state.loadingStatus, isLoaded: true },
                      })),
                    error: (error: HttpErrorResponse) => {
                      errorStore.setError(error);
                      patchState(store, (state) => ({
                        loadingStatus: { ...state.loadingStatus, error },
                      }));
                    },
                    finalize: () =>
                      patchState(store, (state) => ({
                        loadingStatus: { ...state.loadingStatus, isLoading: false },
                      })),
                  }),
                );
              }),
            );
          }),
        ),
      ),
      addResponses(cehrSimulationRequest: Partial<CehrSimulationRequest>): void {
        patchState(store, {
          cehrSimulationRequest: {
            ...store.cehrSimulationRequest(),
            ...cehrSimulationRequest,
          },
        });
      },
      resetSimulation: (): void => {
        patchState(store, initialState);
        store.clearStorage();
      },
    }),
  ),
);
export type ICehrStore = InstanceType<typeof CehrStore>;
